import React from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { circularMedianDeg, median, degToCompass, mod360, mpsToMph } from '../../../utils/windVectorUtils';
import { DEMO_CENTER } from '../../../utils/demo';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function WindMapCard({ transformedData, latestLatLon, gridData, demoMode, rangeLabel, haveInternalWind, haveExternalWind, habRisk }) {
  const source = 'internal';

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: 'pondpage-maps-script',
  });

  const dirKey = source === 'external' ? 'wind_direction_ext' : 'wind_direction_true';
  const spdKey = source === 'external' ? 'wind_speed_ext'     : 'wind_speed';

  const dirs = (transformedData[dirKey] || []).map(r => Number(r.sensor_value));
  const spds = (transformedData[spdKey] || []).map(r => Number(r.sensor_value));

  const computedDir = circularMedianDeg(dirs);
  const computedSpd = median(spds) ?? 0;
  const maxSpd      = spds.filter(Number.isFinite).length ? Math.max(...spds.filter(Number.isFinite)) : 0;

  // Prefer API 6-hour aggregate over computed sensor median
  const useApi   = habRisk?.wind_6h_avg_dir_deg != null;
  const medDir   = useApi ? habRisk.wind_6h_avg_dir_deg : (computedDir ?? 0);
  const medSpd   = useApi ? habRisk.wind_6h_avg_ms      : computedSpd;
  const medDirTo = mod360(medDir + 180);
  const dirLabel = degToCompass(medDir);

  // In demo mode the lake view is pinned to a single fixed geolocation so no
  // real partner coordinates are ever revealed.
  const center = demoMode
    ? { lat: DEMO_CENTER.lat, lng: DEMO_CENTER.lng }
    : { lat: latestLatLon?.lat || gridData?.lat || 0, lng: latestLatLon?.lon || gridData?.lon || 0 };

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.03)', border: '1px solid rgba(0,0,0,.04)', marginBottom: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#94a3b8' }}>
          Wind & Pond Conditions (Past {rangeLabel})
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {/* Google Map */}
        <div style={{ height: 260, borderRadius: 10, overflow: 'hidden', background: '#e8e8e8' }}>
          {isLoaded && center.lat ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={15}
              options={{ disableDefaultUI: true, mapTypeId: 'hybrid' }}
            >
              <Marker position={center} />
            </GoogleMap>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
              Loading map…
            </div>
          )}
        </div>

        {/* Wind compass */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, background: '#f8fafc', borderRadius: 10 }}>
          <div style={{ position: 'relative', width: 120, height: 120, marginBottom: 12 }}>
            {['N','S','W','E'].map((dir, i) => {
              const pos = [{ top: -4, left: '50%', transform: 'translateX(-50%)' },{ bottom: -4, left: '50%', transform: 'translateX(-50%)' },{ left: -4, top: '50%', transform: 'translateY(-50%)' },{ right: -4, top: '50%', transform: 'translateY(-50%)' }][i];
              return <span key={dir} style={{ position: 'absolute', fontSize: 11, fontWeight: 700, color: '#64748b', ...pos }}>{dir}</span>;
            })}
            <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
              <circle cx="60" cy="60" r="48" fill="none" stroke="#d1d5db" strokeWidth="1.5" />
              <g transform={`rotate(${medDirTo}, 60, 60)`}>
                <line x1="60" y1="68" x2="60" y2="20" stroke="#1e2a3a" strokeWidth="2.5" strokeLinecap="round" />
                <polyline points="54,30 60,20 66,30" fill="none" stroke="#1e2a3a" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              </g>
            </svg>
          </div>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', textAlign: 'center', marginBottom: 4 }}>
            6 Hour Dominant Wind Direction: from <strong>{dirLabel}</strong> ({Math.round(medDir)}°)
          </p>
          <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 2 }}>
            {useApi
              ? `Avg: ${mpsToMph(medSpd).toFixed(1)} mph · Max: ${mpsToMph(maxSpd).toFixed(1)} mph`
              : `Median: ${mpsToMph(medSpd).toFixed(1)} mph · Max: ${mpsToMph(maxSpd).toFixed(1)} mph`}
          </p>
          {source === 'external' && (
            <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>External data sourced by Synoptic Data PBC</p>
          )}
        </div>
      </div>
    </div>
  );
}
